/**
 * @file Album Folder Purchase - Uniform Tag Selection container.
 */

import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import get from 'lodash.get';
import { connect } from 'react-redux';
import produce from 'immer';

import { openModal, closeModal } from '../../../actions/modal-actions';
import { AlbumFolderWithIdentityAccess } from '../../../graphql/queries/album-folder';
import AlbumFolderPurchaseUniformTagSelection from '../../../components/purchase/album-folder-purchase/AlbumFolderPurchaseUniformTagSelection';
import { MeWithIdentitiesWithTags } from '../../../graphql/queries/account';
import {
  identitiesFilteredOnAccess,
  sortByIdentityRelationship,
} from '../../../util/identity';
import { AlbumFolderTags } from '../../../graphql/queries/album-folder-tags';
import {
  identityAlbumFolderTagsCreate,
  identityAlbumFolderTagsDelete,
} from '../../../graphql/mutations/album-folder-tags';
import {
  checkoutTagSelection,
  resetTagSelection,
} from '../../../actions/identity-tag-selection';

const withUserData = graphql(MeWithIdentitiesWithTags, {
  options: ({ match }) => ({
    variables: {
      albumFolderId: match.params.albumFolderId,
      albumFolderIds: [match.params.albumFolderId],
    },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { me, loading }, ownProps }) => ({
    loadingIdentities: loading,
    identities: sortByIdentityRelationship(
      identitiesFilteredOnAccess(
        get(me, 'linked_identities.records', []),
        get(ownProps, 'albumFolder.album_identity_access'),
      ),
    ),
    subscribedAlbumFolderIdentities: get(
      me,
      'subscribed_album_folder_identities.records',
      [],
    ),
  }),
});

const withAlbumFolderTags = graphql(AlbumFolderTags, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    loadingTags: loading,
    tags: get(albumFolder, 'tags.records'),
  }),
});

const withAlbumFolder = graphql(AlbumFolderWithIdentityAccess, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    albumFolder,
    loadingAlbumFolder: loading,
  }),
});

const withIdentityAlbumFolderTagsCreate = graphql(
  identityAlbumFolderTagsCreate,
  {
    props: ({ ownProps, mutate }) => ({
      identityAlbumFolderTagsCreateMutation: input =>
        mutate({
          variables: {
            input,
          },
          optimisticResponse: {
            __typename: 'Mutation',
            identityAlbumFolderTagsCreate: {
              __typename: 'IdentityAlbumFolderTagsCreatePayload',
              identity_album_folder_tags: input.album_folder_tags.map(
                tagInfo => {
                  const albumFolderTag = get(ownProps, 'tags', []).find(
                    ({ id }) => id === tagInfo.album_folder_tag_id,
                  );
                  return {
                    album_folder_tag: {
                      ...(albumFolderTag || {}),
                    },
                    id: new Date(),
                    __typename: 'IdentityGradeAlbumFolderTag',
                  };
                },
              ),
            },
          },
          update: (
            store,
            {
              data: {
                identityAlbumFolderTagsCreate: {
                  identity_album_folder_tags: createdTags,
                },
              },
            },
          ) => {
            const { match } = ownProps;

            const data = store.readQuery({
              query: MeWithIdentitiesWithTags,
              variables: {
                albumFolderId: match.params.albumFolderId,
                albumFolderIds: [match.params.albumFolderId],
              },
            });

            const updatedData = produce(data, draft => {
              draft.me.linked_identities.records =
                data.me.linked_identities.records.map(identity => {
                  if (identity.id !== input.identity_id) {
                    return identity;
                  }

                  return {
                    ...identity,
                    album_folder_tags: {
                      ...identity.album_folder_tags,
                      records: [
                        ...createdTags,
                        ...identity.album_folder_tags.records,
                      ],
                    },
                  };
                });
            });

            store.writeQuery({
              query: MeWithIdentitiesWithTags,
              variables: {
                albumFolderId: match.params.albumFolderId,
                albumFolderIds: [match.params.albumFolderId],
              },
              data: updatedData,
            });
          },
        }),
    }),
  },
);

const withIdentityAlbumFolderTagsDelete = graphql(
  identityAlbumFolderTagsDelete,
  {
    props: ({ ownProps, mutate }) => ({
      identityAlbumFolderTagsDeleteMutation: (identityId, ids) =>
        mutate({
          variables: {
            input: {
              ids,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            identityAlbumFolderTagsDelete: {
              __typename: 'IdentityAlbumFolderTagDeletePayload',
              success: true,
            },
          },
          update: store => {
            const { match } = ownProps;

            const data = store.readQuery({
              query: MeWithIdentitiesWithTags,
              variables: {
                albumFolderId: match.params.albumFolderId,
                albumFolderIds: [match.params.albumFolderId],
              },
            });

            const updatedData = produce(data, draft => {
              draft.me.linked_identities.records =
                data.me.linked_identities.records.map(identity => {
                  if (identity.id !== identityId) {
                    return identity;
                  }

                  return {
                    ...identity,
                    album_folder_tags: {
                      ...identity.album_folder_tags,
                      records: identity.album_folder_tags.records.filter(
                        ({ id }) => {
                          return !ids.includes(id);
                        },
                      ),
                    },
                  };
                });
            });

            store.writeQuery({
              query: MeWithIdentitiesWithTags,
              variables: {
                albumFolderId: match.params.albumFolderId,
                albumFolderIds: [match.params.albumFolderId],
              },
              data: updatedData,
            });
          },
        }),
    }),
  },
);

const mapStateToProps = ({ discoverAlbumFolder, identityTagSelection }) => ({
  selectedIdentities: discoverAlbumFolder.identities,
  identitiesWithTags: identityTagSelection.identities,
  success: identityTagSelection.success,
  loadingCheckout: identityTagSelection.loading,
  error: identityTagSelection.error,
  updatedTag: identityTagSelection.updatedTag,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  openModal: (...args) => dispatch(openModal(...args)),
  closeModal: (...args) => dispatch(closeModal(...args)),
  checkout: () =>
    dispatch(
      checkoutTagSelection(ownProps.identityAlbumFolderTagsCreateMutation),
    ),
  resetTagSelection: () => dispatch(resetTagSelection()),
});

export default compose(
  withRouter,
  withAlbumFolder,
  withUserData,
  withAlbumFolderTags,
  withIdentityAlbumFolderTagsCreate,
  withIdentityAlbumFolderTagsDelete,
  connect(mapStateToProps, mapDispatchToProps),
)(AlbumFolderPurchaseUniformTagSelection);
